import React, { useState, useMemo } from 'react';
import { Card } from '../components/ui/Card';
import SafeIcon from '../components/common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { DeadlineCard } from '../components/compliance/DeadlineCard';
import { DeadlineFilters } from '../components/compliance/DeadlineFilters';
import { CategoryFilter } from '../components/compliance/CategoryFilter';
import { EscalationModal } from '../components/compliance/EscalationModal';
import { EscalationTimeline } from '../components/compliance/EscalationTimeline';
import { calculateDeadline } from '../utils/helpers';

export const ComplianceDeadlines = () => {
  const [activeFilter, setActiveFilter] = useState('all');
  const [activeCategory, setActiveCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [escalatingDeadline, setEscalatingDeadline] = useState(null);
  const [showEscalations, setShowEscalations] = useState(false);
  const [escalations, setEscalations] = useState([
    {
      deadlineId: 2,
      type: 'ministerial_alert',
      urgency: 'critical',
      recipient: {
        ministry: 'MWT',
        officer: 'Permanent Secretary - Works & Transport'
      },
      ccAuthorities: ['ppu', 'ag'],
      legalAction: true,
      notes: 'Ministry has failed to acknowledge PPU non-compliance notice within statutory 7-day period. This is the second breach this quarter.',
      attachEvidence: true,
      requestMeeting: true,
      meetingDate: '2025-01-28T10:00',
      timestamp: '2025-01-20T14:30:00Z',
      initiatedBy: 'Prime Minister\'s Office',
      status: 'pending'
    },
    {
      deadlineId: 1,
      type: 'formal_notice',
      urgency: 'high',
      recipient: {
        ministry: 'MWT',
        officer: 'Director of Procurement'
      },
      ccAuthorities: ['ppu'],
      legalAction: false,
      notes: 'Reminder: Contract award publication deadline approaching. Please ensure compliance with PP Regulations 2017.',
      attachEvidence: false,
      requestMeeting: false,
      timestamp: '2025-01-18T09:15:00Z',
      initiatedBy: 'Prime Minister\'s Office',
      status: 'acknowledged'
    }
  ]);

  const allDeadlines = [
    {
      id: 1,
      title: 'Contract Award Publication',
      description: 'Publish contract award notice for Windhoek-Okahandja project on PPU portal',
      startDate: '2025-01-15',
      daysAllowed: 7,
      legalBasis: 'PP Regs 2017, Reg 42(3)',
      category: 'Procurement',
      projectId: 'NAM-2025-0012'
    },
    {
      id: 2,
      title: 'PPU Non-Compliance Acknowledgment',
      description: 'Ministry of Works must acknowledge PPU non-compliance notice',
      startDate: '2025-01-05',
      daysAllowed: 7,
      legalBasis: 'PPA S.7(4)',
      category: 'Audit',
      projectId: 'NAM-2025-0012'
    },
    {
      id: 3,
      title: 'NDP6 Quarterly Progress Report',
      description: 'Submit Q1 2025 progress report to National Planning Commission',
      startDate: '2024-12-31',
      daysAllowed: 30,
      legalBasis: 'NDP6 IMEP Framework',
      category: 'Reporting',
      projectId: null
    },
    {
      id: 4,
      title: 'Treasury Funds Certification',
      description: 'Obtain funds certification for Khomas Hospital Phase 1 procurement',
      startDate: '2025-01-18',
      daysAllowed: 14,
      legalBasis: 'SFA S.14',
      category: 'Financial',
      projectId: 'NAM-2025-0045'
    },
    {
      id: 5,
      title: 'Contract Performance Review',
      description: 'Quarterly performance review for China Jiangsu International',
      startDate: '2025-01-01',
      daysAllowed: 45,
      legalBasis: 'Contract Cl. 8.2',
      category: 'Contract',
      projectId: 'NAM-2025-0045'
    },
    {
      id: 6,
      title: 'Annual Procurement Plan Submission',
      description: 'Ministry of Education must submit FY 2025/26 procurement plan',
      startDate: '2024-12-01',
      daysAllowed: 60,
      legalBasis: 'PPA S.22(1)',
      category: 'Procurement',
      projectId: null
    },
    {
      id: 7,
      title: 'Project Abandonment Investigation',
      description: 'Initiate formal investigation for School Renovation Program: Kunene',
      startDate: '2025-01-10',
      daysAllowed: 21,
      legalBasis: 'Contract Cl. 14.2',
      category: 'Contract',
      projectId: 'NAM-2023-0099'
    },
    {
      id: 8,
      title: 'Environmental Compliance Certificate',
      description: 'Renew environmental clearance for Green Scheme Irrigation Expansion',
      startDate: '2025-01-20',
      daysAllowed: 30,
      legalBasis: 'EMA Act S.27',
      category: 'Contract',
      projectId: 'NAM-2024-0882'
    }
  ];

  const { filteredDeadlines, stats } = useMemo(() => {
    const enrichedDeadlines = allDeadlines.map(d => ({
      ...d,
      ...calculateDeadline(d.startDate, d.daysAllowed)
    }));

    const stats = {
      total: enrichedDeadlines.length,
      overdue: enrichedDeadlines.filter(d => d.isOverdue).length,
      critical: enrichedDeadlines.filter(d => !d.isOverdue && d.severity === 'critical').length,
      warning: enrichedDeadlines.filter(d => !d.isOverdue && d.severity === 'warning').length,
      normal: enrichedDeadlines.filter(d => !d.isOverdue && d.severity === 'normal').length
    };

    let filtered = enrichedDeadlines;

    if (activeFilter !== 'all') {
      if (activeFilter === 'overdue') {
        filtered = filtered.filter(d => d.isOverdue);
      } else {
        filtered = filtered.filter(d => !d.isOverdue && d.severity === activeFilter);
      }
    }

    if (activeCategory !== 'all') {
      filtered = filtered.filter(d => d.category === activeCategory);
    }

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(d => 
        d.title.toLowerCase().includes(query) ||
        d.description.toLowerCase().includes(query) ||
        d.legalBasis.toLowerCase().includes(query) ||
        (d.projectId && d.projectId.toLowerCase().includes(query))
      );
    }

    filtered.sort((a, b) => {
      if (a.isOverdue && !b.isOverdue) return -1;
      if (!a.isOverdue && b.isOverdue) return 1;
      return a.daysRemaining - b.daysRemaining;
    });

    return { filteredDeadlines: filtered, stats };
  }, [allDeadlines, activeFilter, activeCategory, searchQuery]);

  const handleEscalate = (deadline) => {
    setEscalatingDeadline(deadline);
  };

  const handleEscalationSubmit = (escalation) => {
    setEscalations(prev => [escalation, ...prev]);
    setEscalatingDeadline(null);
    alert('Escalation submitted successfully! All parties have been notified.');
  };

  return (
    <div className="space-y-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Compliance Deadline Dashboard</h1>
          <p className="text-slate-500">Monitor statutory deadlines and legal obligations</p>
        </div>
        <div className="flex gap-3">
          <button 
            onClick={() => setShowEscalations(!showEscalations)}
            className="px-4 py-2 border border-slate-300 rounded-lg text-sm font-medium hover:bg-slate-50 flex items-center gap-2"
          >
            <SafeIcon icon={FiIcons.FiList} />
            {showEscalations ? 'View Deadlines' : 'View Escalations'}
            {escalations.length > 0 && (
              <span className="px-2 py-0.5 bg-red-100 text-red-700 text-xs font-bold rounded-full">
                {escalations.length}
              </span>
            )}
          </button>
          <button className="px-4 py-2 border border-slate-300 rounded-lg text-sm font-medium hover:bg-slate-50 flex items-center gap-2">
            <SafeIcon icon={FiIcons.FiDownload} />
            Export Report
          </button>
          <button className="px-4 py-2 bg-grn-navy text-white rounded-lg text-sm font-medium hover:bg-blue-900 flex items-center gap-2">
            <SafeIcon icon={FiIcons.FiPlus} />
            Add Deadline
          </button>
        </div>
      </div>

      {showEscalations ? (
        /* Escalation Timeline View */
        <>
          <Card className="bg-red-50 border-red-200">
            <div className="flex gap-3">
              <SafeIcon icon={FiIcons.FiAlertTriangle} className="text-red-600 text-xl shrink-0 mt-0.5" />
              <div className="text-sm text-red-900">
                <p className="font-bold mb-1">Escalation Log</p>
                <p className="text-red-700">
                  All escalations are permanently logged and subject to parliamentary oversight. 
                  Total escalations: <strong>{escalations.length}</strong>
                </p>
              </div>
            </div>
          </Card>

          <Card title="Escalation Timeline">
            <EscalationTimeline escalations={escalations} />
          </Card>
        </>
      ) : (
        /* Deadline Cards View */
        <>
          {/* Stats Overview */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <Card className="text-center p-4">
              <div className="text-3xl font-black text-slate-900">{stats.total}</div>
              <div className="text-xs text-slate-500 uppercase tracking-wider mt-1">Total</div>
            </Card>
            <Card className="text-center p-4 border-l-4 border-l-red-600">
              <div className="text-3xl font-black text-red-700">{stats.overdue}</div>
              <div className="text-xs text-slate-500 uppercase tracking-wider mt-1">Overdue</div>
            </Card>
            <Card className="text-center p-4 border-l-4 border-l-orange-500">
              <div className="text-3xl font-black text-orange-700">{stats.critical}</div>
              <div className="text-xs text-slate-500 uppercase tracking-wider mt-1">Critical</div>
            </Card>
            <Card className="text-center p-4 border-l-4 border-l-amber-500">
              <div className="text-3xl font-black text-amber-700">{stats.warning}</div>
              <div className="text-xs text-slate-500 uppercase tracking-wider mt-1">Warning</div>
            </Card>
            <Card className="text-center p-4 border-l-4 border-l-green-500">
              <div className="text-3xl font-black text-green-700">{stats.normal}</div>
              <div className="text-xs text-slate-500 uppercase tracking-wider mt-1">On Track</div>
            </Card>
          </div>

          {/* Filters */}
          <Card>
            <div className="space-y-4">
              <div>
                <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-3">
                  Filter by Status
                </h3>
                <DeadlineFilters 
                  activeFilter={activeFilter}
                  onFilterChange={setActiveFilter}
                  stats={stats}
                />
              </div>
              
              <div className="border-t border-slate-200 pt-4">
                <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-3">
                  Filter by Category
                </h3>
                <CategoryFilter 
                  activeCategory={activeCategory}
                  onCategoryChange={setActiveCategory}
                />
              </div>

              <div className="border-t border-slate-200 pt-4">
                <div className="relative">
                  <SafeIcon 
                    icon={FiIcons.FiSearch} 
                    className="absolute left-3 top-2.5 text-slate-400" 
                  />
                  <input
                    type="text"
                    placeholder="Search deadlines, legal basis, or project ID..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-grn-navy/20"
                  />
                </div>
              </div>
            </div>
          </Card>

          {/* Results Header */}
          <div className="flex justify-between items-center">
            <h2 className="text-sm font-bold text-slate-700">
              Showing {filteredDeadlines.length} deadline{filteredDeadlines.length !== 1 ? 's' : ''}
            </h2>
            <select className="px-3 py-1.5 border border-slate-300 rounded-lg text-xs bg-white">
              <option>Sort by: Due Date (Earliest)</option>
              <option>Sort by: Due Date (Latest)</option>
              <option>Sort by: Category</option>
              <option>Sort by: Project</option>
            </select>
          </div>

          {/* Deadline Cards Grid */}
          {filteredDeadlines.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredDeadlines.map(deadline => (
                <DeadlineCard
                  key={deadline.id}
                  {...deadline}
                  onEscalate={() => handleEscalate(deadline)}
                />
              ))}
            </div>
          ) : (
            <Card className="text-center py-12">
              <div className="flex flex-col items-center justify-center text-slate-400">
                <SafeIcon icon={FiIcons.FiCheckCircle} className="text-4xl mb-3" />
                <h3 className="text-lg font-bold text-slate-600 mb-1">No deadlines found</h3>
                <p className="text-sm">Try adjusting your filters or search query</p>
              </div>
            </Card>
          )}

          {/* Legal Reference */}
          <Card className="bg-blue-50 border-blue-200">
            <div className="flex gap-3">
              <SafeIcon icon={FiIcons.FiInfo} className="text-grn-navy text-xl shrink-0 mt-0.5" />
              <div className="text-xs text-slate-700">
                <p className="font-bold mb-1">Legal Framework Reference</p>
                <p>
                  <strong>PPA:</strong> Public Procurement Act (2015) • 
                  <strong className="ml-2">SFA:</strong> State Finance Act (1991) • 
                  <strong className="ml-2">PP Regs:</strong> Public Procurement Regulations (2017) • 
                  <strong className="ml-2">EMA:</strong> Environmental Management Act (2007)
                </p>
              </div>
            </div>
          </Card>
        </>
      )}

      {/* Escalation Modal */}
      {escalatingDeadline && (
        <EscalationModal
          deadline={escalatingDeadline}
          onClose={() => setEscalatingDeadline(null)}
          onSubmit={handleEscalationSubmit}
        />
      )}
    </div>
  );
};